import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Revenue-Kalkulator per Empfehlung.
 * Formel: (monatlicher Umsatz * uplift_pct/100) * 12 = jährliches Umsatz-Delta
 */
export default function RevenueImpact({ shopId, upliftPct, variant = "compact" }) {
  const { t, lang } = useLang();

  const { data: ctx } = useQuery({
    queryKey: ["revenue-context", shopId],
    queryFn: async () => (await api.get(`/shops/${shopId}/revenue-context`)).data,
    enabled: !!shopId,
    staleTime: 5 * 60 * 1000,
  });

  const fmt = (n) =>
    new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n || 0);

  if (!ctx || !upliftPct) return null;

  const monthlyDelta = ctx.monthly_revenue_eur * (upliftPct / 100);
  const annualDelta = Math.round(monthlyDelta * 12);
  const monthlyDeltaRounded = Math.round(monthlyDelta);

  if (variant === "banner") {
    return (
      <div
        data-testid="revenue-impact-banner"
        className="rounded-lg border border-[hsl(var(--chart-3))]/30 bg-[hsl(var(--chart-3))]/[0.05] p-4 mb-5"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 shrink-0 rounded-md bg-[hsl(var(--chart-3))]/10 grid place-items-center">
            <TrendingUp className="h-5 w-5 text-[hsl(var(--chart-3))]" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {t("revenue_impact_title")}
            </div>
            <div className="text-2xl font-display font-semibold text-[hsl(var(--chart-3))] mt-0.5">
              {fmt(annualDelta)}<span className="text-sm ml-1 font-mono font-normal text-muted-foreground">/{t("revenue_year")}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1 font-mono">
              ≈ {fmt(monthlyDeltaRounded)}/{t("revenue_month")} &middot; +{upliftPct}% auf {fmt(ctx.monthly_revenue_eur)}/{t("revenue_month")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="revenue-impact-compact"
      className={cn("inline-flex items-center gap-1.5 text-xs font-mono", "text-[hsl(var(--chart-3))]")}
    >
      <TrendingUp className="h-3 w-3" />
      <span className="font-semibold">≈ {fmt(annualDelta)}</span>
      <span className="text-muted-foreground">/{t("revenue_year")}</span>
    </div>
  );
}
